// src/context/SupportContext.tsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supportService } from '../services/api/support.service';
import { useAuth } from './AuthContext';

interface SupportContextType {
  unreadCount: number;
  lastReadMessages: Record<string, string>; // ticketId -> lastMessageId
  refreshUnreadCount: () => Promise<void>;
  markMessagesAsRead: (ticketId: string, lastMessageId: string) => void;
  isRefreshing: boolean;
}

const SupportContext = createContext<SupportContextType | undefined>(undefined);

export function SupportProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessages, setLastReadMessages] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load last read messages from localStorage on mount
  useEffect(() => {
    if (user?.id) {
      const stored = localStorage.getItem(`support_read_${user.id}`);
      if (stored) {
        setLastReadMessages(JSON.parse(stored));
      }
    }
  }, [user?.id]);

  // Save last read messages to localStorage when they change
  useEffect(() => {
    if (user?.id && Object.keys(lastReadMessages).length > 0) {
      localStorage.setItem(`support_read_${user.id}`, JSON.stringify(lastReadMessages));
    }
  }, [lastReadMessages, user?.id]);

  // Calculate unread count from tickets
  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      setIsRefreshing(true);
      
      // Get all open tickets (not closed)
      const response = await supportService.getUserTickets({
        limit: 100, // Get more tickets to check for unread messages
        offset: 0
      });

      let totalUnread = 0;

      // Check each ticket for unread messages
      for (const ticket of response.tickets) {
        // Skip closed tickets
        if (ticket.status === 'CLOSED') continue;

        // Get full ticket details with messages
        try {
          const ticketDetails = await supportService.getTicketDetails(ticket.id);
          
          // Check if there are messages
          if (ticketDetails.messages && ticketDetails.messages.length > 0) {
            const lastReadId = lastReadMessages[ticket.id];
            
            if (lastReadId) {
              // Count messages after the last read one
              const lastReadIndex = ticketDetails.messages.findIndex(m => m.id === lastReadId);
              if (lastReadIndex !== -1) {
                const unreadMessages = ticketDetails.messages.slice(lastReadIndex + 1)
                  .filter(m => m.userId !== user.id); // Only count support messages
                totalUnread += unreadMessages.length;
              }
            } else {
              // All support messages are unread
              const unreadMessages = ticketDetails.messages.filter(m => m.userId !== user.id);
              totalUnread += unreadMessages.length;
            }
          }
        } catch (err) {
          console.error(`Error fetching details for ticket ${ticket.id}:`, err);
        }
      }

      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error refreshing unread count:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isAuthenticated, user, lastReadMessages]);

  // Mark messages as read for a specific ticket
  const markMessagesAsRead = useCallback((ticketId: string, lastMessageId: string) => {
    setLastReadMessages(prev => ({
      ...prev,
      [ticketId]: lastMessageId
    }));
    // Refresh count after marking as read
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  // Refresh unread count on mount and when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshUnreadCount();
    }
  }, [isAuthenticated, user]);

  // Auto-refresh every 30 seconds when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUnreadCount]);

  const value = {
    unreadCount,
    lastReadMessages,
    refreshUnreadCount,
    markMessagesAsRead,
    isRefreshing
  };

  return (
    <SupportContext.Provider value={value}>
      {children}
    </SupportContext.Provider>
  );
}

export function useSupport() {
  const context = useContext(SupportContext);
  if (context === undefined) {
    throw new Error('useSupport must be used within a SupportProvider');
  }
  return context;
}